import type { LayerData, OptimizedBrick } from "./brickOptimizer";

// LDraw geometry units: 1 stud = 20 LDU, a brick is 24 LDU tall, a plate 8.
// Parts are modeled with the origin at the bottom face, extending up into
// -Y, so a mosaic on 32x32 baseplates (bottom at y=0) puts layer n at
// y = -(8 + 24n).
const LDU_PER_STUD = 20;
const BRICK_HEIGHT_LDU = 24;
const PLATE_HEIGHT_LDU = 8;
const BASEPLATE_STUDS = 32;

// LDraw design IDs keyed by "<depth>x<width>" in part-local terms (depth
// runs along local Z, width along local X) with a trailing "p" for plates.
export const LDR_PARTS: Record<string, string> = {
  "1x1": "3005",
  "1x2": "3004",
  "1x3": "3622",
  "1x4": "3010",
  "1x6": "3009",
  "1x8": "3008",
  "2x2": "3003",
  "2x3": "3002",
  "2x4": "3001",
  "2x6": "2456",
  "2x8": "3007",
  "4x4": "3011",
  "4x6": "30414",
  "4x8": "30413",
  "1x1p": "3024",
  "1x2p": "3023",
  "1x3p": "3623",
  "1x4p": "3710",
  "1x6p": "3666",
  "1x8p": "3460",
  "2x2p": "3022",
  "2x3p": "3021",
  "2x4p": "3020",
  "2x6p": "3795",
  "2x8p": "3034",
  "4x4p": "3031",
  "4x6p": "3032",
  "4x8p": "3035",
};

// Palette name -> LDraw colour code (LDConfig). Default is White (15).
const LDR_COLOR_CODES: Record<string, number> = {
  White: 15,
  "Dark Stone Gray": 72,
  "Bright Red": 4,
  "Dark Green": 2,
  "Earth Blue": 272,
  "Bright Yellow": 14,
  "Flame Orangey Yellow": 106,
  "Medium Lavender": 85,
  "Reddish Brown": 70,
  "Light Stone Gray": 71,
  "Medium Stone Gray": 73,
  "Cool Yellow": 226,
  Silver: 287,
  Orange: 25,
  "Sand Blue": 379,
  "Sand Yellow": 28,
  "Medium Azur": 322,
  "Bright Green": 10,
  "Bright Yellowish Green": 27,
  "Earth Green": 288,
  Nougat: 450,
};

const DEFAULT_COLOR_CODE = 15;
const IDENTITY = "1 0 0 0 1 0 0 0 1";
const ROTATE_Y_90 = "0 0 1 0 1 0 -1 0 0";

interface BrickFootprint {
  width: number;
  depth: number;
}

const footprintOf = (brick: OptimizedBrick): BrickFootprint =>
  brick.rotation === 90
    ? { width: brick.brickType.height, depth: brick.brickType.width }
    : { width: brick.brickType.width, depth: brick.brickType.height };

// LDraw parts are named "<depth>x<width>", our brickType dims are grid
// "<width>x<height>", so the part may need a 90-degree turn to fit.
const partFor = (
  width: number,
  depth: number,
  suffix: string,
): { part: string; rotate: boolean } | null => {
  const direct = LDR_PARTS[`${depth}x${width}${suffix}`];
  if (direct) return { part: direct, rotate: false };
  const flipped = LDR_PARTS[`${width}x${depth}${suffix}`];
  if (flipped) return { part: flipped, rotate: true };
  return null;
};

const colorCodeFor = (colorName: string) =>
  LDR_COLOR_CODES[colorName] ?? DEFAULT_COLOR_CODE;

const brickLines = (brick: OptimizedBrick): string[] => {
  const code = colorCodeFor(brick.colorName);
  const suffix = brick.brickType.id.endsWith("p") ? "p" : "";
  const footprint = footprintOf(brick);
  const y = -(PLATE_HEIGHT_LDU + brick.z * BRICK_HEIGHT_LDU);
  const part = partFor(footprint.width, footprint.depth, suffix);

  if (part) {
    const x = (brick.x + footprint.width / 2) * LDU_PER_STUD;
    const z = (brick.y + footprint.depth / 2) * LDU_PER_STUD;
    return [
      `1 ${code} ${x} ${y} ${z} ${part.rotate ? ROTATE_Y_90 : IDENTITY} ${part.part}.dat`,
    ];
  }

  // Unknown footprint: fall back to one 1x1 brick per covered cell.
  const lines: string[] = [];
  for (let dy = 0; dy < footprint.depth; dy++) {
    for (let dx = 0; dx < footprint.width; dx++) {
      const x = (brick.x + dx + 0.5) * LDU_PER_STUD;
      const z = (brick.y + dy + 0.5) * LDU_PER_STUD;
      lines.push(`1 ${code} ${x} ${y} ${z} ${IDENTITY} 3005.dat`);
    }
  }
  return lines;
};

const gridExtent = (layers: LayerData[]): BrickFootprint => {
  let width = 0;
  let depth = 0;
  for (const layer of layers) {
    for (const brick of layer.bricks) {
      const footprint = footprintOf(brick);
      width = Math.max(width, brick.x + footprint.width);
      depth = Math.max(depth, brick.y + footprint.depth);
    }
  }
  return { width, depth };
};

const baseplateLines = (extent: BrickFootprint): string[] => {
  const cols = Math.ceil(extent.width / BASEPLATE_STUDS);
  const rows = Math.ceil(extent.depth / BASEPLATE_STUDS);
  const lines: string[] = [];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const x = (col + 0.5) * BASEPLATE_STUDS * LDU_PER_STUD;
      const z = (row + 0.5) * BASEPLATE_STUDS * LDU_PER_STUD;
      lines.push(`1 15 ${x} 0 ${z} ${IDENTITY} 3811.dat`);
    }
  }
  return lines;
};

export interface LDrawExportOptions {
  includeBaseplate?: boolean;
  modelName?: string;
}

export function layersToLDraw(
  layers: LayerData[],
  options: LDrawExportOptions = {},
): string {
  const { includeBaseplate = true, modelName = "bricksup-mosaic.ldr" } =
    options;

  const out: string[] = [
    `0 FILE ${modelName}`,
    "0 bricksup mosaic",
    `0 Name: ${modelName}`,
    "0 Author: bricksup",
    "0 !LDRAW_ORG Model",
    "0 // Generated by bricksup",
  ];

  layers.forEach((layer, i) => {
    if (i === 0 && includeBaseplate) {
      out.push(...baseplateLines(gridExtent(layers)));
    }
    out.push(...layer.bricks.flatMap(brickLines));
    if (i < layers.length - 1) out.push("0 STEP");
  });

  return out.join("\r\n") + "\r\n";
}

export function exportLDraw(
  layers: LayerData[],
  filename = "bricksup-mosaic.ldr",
): void {
  const ldr = layersToLDraw(layers, { modelName: filename });
  const dataUri = "data:text/plain;charset=utf-8," + encodeURIComponent(ldr);

  const linkElement = document.createElement("a");
  linkElement.setAttribute("href", dataUri);
  linkElement.setAttribute("download", filename);
  linkElement.click();
}
